import { KIT_COPPER_METRES, STOCK } from '../data/pricing';

// Cart store. Plain localStorage plus an event, so every component that reads
// the cart stays in sync without pulling in a state library.
//
// A line is one model in one configuration. The same model bought with-kit and
// without-kit is two lines, because they are two different SKUs that ship
// differently.

const KEY = 'cc_cart_v1';
const EVENT = 'cc_cart_change';
const OPEN = 'cc_cart_open';

export function newLine(modelId, kit, qty = 1) {
  return {
    lineId: `${modelId}:${kit}:${Date.now().toString(36)}`,
    modelId,
    kit,
    qty: Math.max(1, qty),
    kitAddon: false,      // buying the original kit alongside a without-kit unit
    // Installation is opt-in. Ticking it for the customer puts charges in the
    // total they never asked for, and plenty of buyers have their own fitter.
    withInstall: false,
    knowsMeasurements: false,
    copperM: '',          // three separate runs — they are never the same length
    drainM: '',
    wireM: '',
    standSplit15: false,
    dismantleOld: false,
    antiCorrosion: false,
    // Exchange of an old unit. Off by default — it needs the customer to tell
    // us the old machine's tonnage and coil, so it cannot be assumed.
    exchange: false,
    exchangeTon: '',
    exchangeCoil: '',
  };
}

export function readCart() {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCart(lines) {
  try {
    localStorage.setItem(KEY, JSON.stringify(lines));
  } catch {
    /* storage disabled — cart just won't persist across reloads */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: lines }));
  }
  return lines;
}

// Units in hand for one variant. A cart line never asks for more than this;
// the payment server checks the live count again before taking money.
export const stockFor = (modelId, kit) => (STOCK[modelId] || {})[kit] || 0;

// Adding the same model in the same kit configuration bumps quantity rather
// than creating a second identical line.
export function addToCart(modelId, kit, qty = 1) {
  // Only units in the godown are sold online. Anything else is a request,
  // handled on /request-unit, so a cart can never hold something that
  // cannot be paid for and dispatched.
  const have = stockFor(modelId, kit);
  if (have <= 0) return readCart();
  const lines = readCart();
  const match = lines.find((l) => l.modelId === modelId && l.kit === kit);
  if (match) {
    match.qty = Math.min(have, 99, match.qty + qty);
    return writeCart([...lines]);
  }
  return writeCart([...lines, newLine(modelId, kit, Math.min(have, qty))]);
}

export const updateLine = (lineId, patch) =>
  writeCart(readCart().map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)));

export const setQty = (lineId, qty) =>
  qty <= 0
    ? removeLine(lineId)
    : writeCart(readCart().map((l) => (l.lineId === lineId
      ? { ...l, qty: Math.max(1, Math.min(99, stockFor(l.modelId, l.kit) || 99, qty)) }
      : l)));

export const removeLine = (lineId) =>
  writeCart(readCart().filter((l) => l.lineId !== lineId));

export const clearCart = () => writeCart([]);

export const cartCount = (lines = readCart()) =>
  lines.reduce((s, l) => s + (Number(l.qty) || 0), 0);

// Drawer control. Kept here so any component can open the cart without
// prop-drilling a handler down through the grid.
export const openCart = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(OPEN));
};

export function subscribeOpen(fn) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener(OPEN, fn);
  return () => window.removeEventListener(OPEN, fn);
}

export function subscribe(fn) {
  if (typeof window === 'undefined') return () => {};
  const onChange = () => fn(readCart());
  window.addEventListener(EVENT, onChange);
  window.addEventListener('storage', onChange);   // keeps other tabs in step
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

export { KIT_COPPER_METRES };